import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, Self, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent, StepsComponent } from '@app/components';
import { FilterGroup as BaseFilterGroup } from '@app/components/filter/filter.component';
import { OptionStatus } from '@app/components/platform/employee-autocomplete/employee-autocomplete.component';
import { Columns as Column, Row } from '@app/components/simple-table/simple-table.component';
import { AnalyticEvents, FeatureFlag, SocketEventTypes } from '@app/enums';
import { JsonApiResponse } from '@app/interfaces/json-api-resource.interface';
import { Department } from '@app/models/company/department.model';
import { Office } from '@app/models/company/office.model';
import { QueryFetcher } from '@app/models/core/query-fetcher.model';
import { CompanyAdditionalIncome } from '@app/models/payroll/company-additional-income.model';
import { EmployeeHour } from '@app/models/payroll/company/employee-hour.model';
import { Paystub } from '@app/models/payroll/company/paystub.model';
import { Payroll } from '@app/models/payroll/payroll.model';
import { Schedule } from '@app/models/payroll/schedule.model';
import { PayrollFilterCategories } from '@app/modules/payroll/interfaces/payroll-filters.interface';
import { PayrollRunStatus } from '@app/modules/payroll/interfaces/payroll-run-status.interface';
import { PayrollResources } from '@app/modules/payroll/payroll.resources';
import { AnalyticService, AuthService, NotifyService, SocketService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { LocalStorageService } from '@app/services/local-storage/local-storage.service';
import { PayrollSettingsService, Settings } from '@app/services/payroll-settings.service';
import { SortDirection } from '@app/types';
import { UpdateView } from '@common/views';
import { EditPayrollForm, PayrollEditForm, PayrollHoursForm } from '@forms/payroll/components';
import { Employee } from '@models/employee/employee.model';
import { TranslateService } from '@ngx-translate/core';
import { PayrollStates } from '@payroll/enums/payroll-state.enum';
import { TimeWorkedImportOngoing, isOngoing } from '@payroll/interfaces/time-worked-import.interface';
import {
    AmeegoHumiImportService,
    CheckForSchedulingImportService,
    CheckForWcbBackfillService,
    HoursImporterService,
    ImportAdditionalIncomesService,
    PaystubLazyLoadService,
} from '@payroll/services';
import { PayrollService } from '@payroll/services/payroll.service';
import { getYear, parse } from 'date-fns';
import { debounce, isNil } from 'lodash-es';
import { Subscription } from 'rxjs';

export type FilterGroup = BaseFilterGroup & { category: keyof PayrollFilterCategories };

const FILTERS = {
    OFFICE: { label: 'payroll.run.officeLocation', category: 'officeId' },
    DEPARTMENT: { label: 'payroll.run.department', category: 'departmentId' },
    COMPENSATION_TYPE: { label: 'payroll.run.payBy', category: 'compensationType' },
    ADDITIONAL_INCOME: { label: 'payroll.run.otherIncomeTypes', category: 'additionalIncome' },
    PAYMENTS: { label: 'payroll.run.payments', category: 'payments' },
    STATUS: { label: 'payroll.run.status', category: 'status' },
    WAGE_TYPE: { label: 'payroll.run.compensationType', category: 'wageType' },
} as const;

export interface ProgressBarStatus {
    completed: number;
    total: number;
}

/**
 * Transform filter groups to payroll filter categories
 */
const filterGroupsToFilterCategories = (groups: FilterGroup[]): Partial<PayrollFilterCategories> => {
    const entries = groups
        .map(({ category, options }) => {
            const filterValues = options.filter(({ selected }) => selected).map(({ value }) => value);

            return [category, filterValues];
        })
        .filter(([, filterValues]) => filterValues.length > 0);

    return Object.fromEntries(entries);
};

const DEBOUNCE_TIME = 1000;

type Step = 'hoursStep' | 'editStep' | 'reviewStep';

@Component({
    templateUrl: './payroll-update.template.html',
    styleUrls: ['./payroll-update.style.scss'],
    providers: [PaystubLazyLoadService],
})
export class PayrollUpdateView extends UpdateView implements OnInit, OnDestroy {
    @ViewChild('completeAndPayConfirmation', { static: true })
    completeAndPayConfirmation!: ConfirmationDialogComponent;

    @ViewChild('confirmDeleteDialog', { static: true })
    confirmDeleteDialog!: ConfirmationDialogComponent;

    @ViewChild('appSteps') stepsComponent!: StepsComponent;

    @ViewChild('editForm') editForm!: PayrollEditForm;
    @ViewChild('editFormAgGrid') editFormAgGrid!: EditPayrollForm;
    @ViewChild('hoursForm') hoursForm!: PayrollHoursForm;

    @ViewChild('terminatedSalaryConfirmation', { static: true })
    terminatedSalaryConfirmation!: ConfirmationDialogComponent;

    @ViewChild('scrollAnchor') scrollAnchor!: ElementRef<HTMLElement>;
    scrollObserver?: IntersectionObserver;

    payroll?: Payroll;
    addEmployee?: Employee;
    isLoading = true;
    isHoursUnlocked = false;
    isLazyLoading = false;
    offCycleCustomEmployeeQuery: QueryFetcher;
    settings?: Settings;
    showHoursStep = false;
    showHoursForm = true;
    showEditForm = true;
    expandExportMenu = false;
    expandImportPayrollData = false;
    stepIndices: Record<Step, number | null> = {
        hoursStep: null,
        editStep: 0,
        reviewStep: 1,
    };
    initialStep = this.stepIndices.editStep;

    officeFilterGroup: FilterGroup = {
        ...FILTERS.OFFICE,
        options: [],
    };

    departmentFilterGroup: FilterGroup = {
        ...FILTERS.DEPARTMENT,
        options: [],
    };

    compensationTypeFilterGroup: FilterGroup = {
        ...FILTERS.COMPENSATION_TYPE,
        options: [
            { label: 'payroll.run.directDeposit', value: 'deposit', selected: false },
            { label: 'payroll.run.cheque', value: 'cheque', selected: false },
        ],
    };

    additionalIncomeFilterGroup: FilterGroup = {
        ...FILTERS.ADDITIONAL_INCOME,
        options: [],
    };

    paymentsFilterGroup: FilterGroup = {
        ...FILTERS.PAYMENTS,
        options: [{ label: 'payroll.run.reimbursement', value: 'reimbursement', selected: false }],
    };

    statusFilterGroup: FilterGroup = {
        ...FILTERS.STATUS,
        options: [
            { label: 'payroll.run.newHire', value: 'newHire', selected: false },
            { label: 'payroll.run.terminated', value: 'terminated', selected: false },
            { label: 'payroll.run.rehired', value: 'rehired', selected: false },
        ],
    };

    filterGroups = [
        this.officeFilterGroup,
        this.departmentFilterGroup,
        this.compensationTypeFilterGroup,
        this.additionalIncomeFilterGroup,
        this.paymentsFilterGroup,
        this.statusFilterGroup,
    ];

    hasAgGridFeature = false;
    showTableView = false;
    completed = 0;
    total = 0;
    hasHoursRunCompleted = false;
    hasPayrollRunCompleted = false;

    disableUserFromClickingNextStep = false;
    schedule?: Schedule;
    isUsingTimeWorkedImportPolling = false;
    ameegoHumiImport = false;
    ameegoHumiImportErrors = [];
    ameegoHumiImportErrorsModalShow = false;
    ameegoHumiImportErrorsModalColumns: Column[] = [
        {
            columnName: 'payroll.run.ameegoUploadErrorModalHeader1',
            columnSpan: 'columns-3',
        },
        {
            columnName: 'payroll.run.ameegoUploadErrorModalHeader2',
            columnSpan: 'columns-8',
        },
    ];
    ameegoHumiImportErrorsModalRows: Row[] = [];

    importsInProcess: TimeWorkedImportOngoing[] = [];
    importsJustFinished = false;

    isWcbBackfillInProgress = false;
    wcbBackfillJustFinished = false;
    showRunStepContent = false;

    // share feedback
    feedBackMessage = '';
    isFeedbackModalOpen = false;
    private readonly feedbackZapierUrl = 'https://hooks.zapier.com/hooks/catch/1592077/29drgu1';

    private subscriptions: Subscription[] = [];

    constructor(
        public auth: AuthService,
        private ameegoHumiImportService: AmeegoHumiImportService,
        private analytics: AnalyticService,
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private hoursImporterService: HoursImporterService,
        private importAdditionalIncomesService: ImportAdditionalIncomesService,
        public settingsService: PayrollSettingsService,
        private payrollService: PayrollService,
        private featureService: FeatureService,
        private socket: SocketService,
        private http: HttpClient,
        private checkImportService: CheckForSchedulingImportService,
        private checkForWcbBackfillService: CheckForWcbBackfillService,
        @Self() private paystubService: PaystubLazyLoadService,
        private translateService: TranslateService,
        private localStorageService: LocalStorageService
    ) {
        super();

        this.offCycleCustomEmployeeQuery = this.payrollService.offCycleCustomEmployeeQuery();

        this.subscriptions.push(
            this.socket
                .getCompanyChannel(SocketEventTypes.payrollRunProgress)
                .subscribe((runStatus: PayrollRunStatus) => {
                    this.hasPayrollRunCompleted = false;
                    this.updateProgress(runStatus);
                }),

            this.socket
                .getCompanyChannel(SocketEventTypes.payrollRunError)
                .subscribe(async (errorMessage) => this.payrollRunError(errorMessage)),

            this.socket.getCompanyChannel(SocketEventTypes.payrollRunCompleted).subscribe(async () => {
                this.isLoading = true;
                await this.finishRunPayroll();
                this.isLoading = false;
                this.stepsComponent.activeStep = this.stepIndices.reviewStep as number;
            }),

            this.socket
                .getCompanyChannel(SocketEventTypes.payrollRunHoursProgress)
                .subscribe((progress: PayrollRunStatus) => this.updateProgress(progress)),

            this.socket.getCompanyChannel(SocketEventTypes.payrollRunHoursCompleted).subscribe(async () => {
                this.updateProgress({ completed: 1, total: 1 });
                this.hasHoursRunCompleted = true;

                this.isLoading = true;
                this.paystubService.resetQuery();
                await this.paystubService.load();
                this.isLoading = false;

                this.stepsComponent.setStep(this.stepIndices.editStep);
            }),

            this.socket
                .getCompanyChannel(SocketEventTypes.payrollRunHoursError)
                .subscribe(async (errorMessage) => this.payrollRunError(errorMessage))
        );
    }

    @HostListener('window:beforeunload', ['$event'])
    warn($event: BeforeUnloadEvent): void {
        if (!this.hasPayrollRunCompleted) {
            $event.returnValue = this.translateService.instant(
                'payroll.run.areYouSureYouWantToLeaveThisPagePayrollRunIsStillInProgressButIt'
            );
        }
    }

    async getFeatureFlags(): Promise<void> {
        const [isUsingTimeWorkedImportPolling, editPayrollAgGrid, ameegoHumiImport] = await this.featureService.hasMany(
            FeatureFlag.timeWorkedImportPolling,
            FeatureFlag.editPayrollAgGrid,
            FeatureFlag.ameegoHumiImport
        );
        this.isUsingTimeWorkedImportPolling = isUsingTimeWorkedImportPolling;
        this.hasAgGridFeature = editPayrollAgGrid;
        this.ameegoHumiImport = ameegoHumiImport;
    }

    nextStepTransitionState = async (): Promise<void> => {
        if (this.stepsComponent.activeStep === this.stepIndices.hoursStep) {
            this.showHoursForm = false;
            this.hasHoursRunCompleted = false;

            try {
                await this.payrollService.asyncRunHours(this.getPayroll());
            } catch (error) {
                this.notify.error(error);
            }

            // we will manually move to the next step upon progress bar completion
            return Promise.reject();
        }

        if (this.stepsComponent.activeStep === this.stepIndices.editStep) {
            this.hasPayrollRunCompleted = false;

            if (this.hasTerminatedEmployeeOnOffCycle(this.getPayroll(), this.paystubService.payStubs.getValue())) {
                await this.terminatedSalaryConfirmation.open().then((yes: boolean) => {
                    if (!yes) {
                        return Promise.reject();
                    }
                });
            }

            this.updateProgress({ total: 1, completed: 0 });
            this.showEditForm = false;

            try {
                await this.payrollService.asyncRun(this.getPayroll());
            } catch (error) {
                this.notify.error(error);
            }

            // we will manually move to the next step upon progress bar completion
            return Promise.reject();
        }
    };

    previousStepTransitionState = async (): Promise<void> => {
        if (this.stepsComponent.activeStep - 1 === this.stepIndices.hoursStep) {
            this.showHoursForm = true;
            this.hasHoursRunCompleted = false;
            this.completed = 0;
            this.hoursForm.reloadTables();
        }
        if (this.stepsComponent.activeStep - 1 === this.stepIndices.editStep) {
            this.showEditForm = true;
            await this.onReset();
        }
    };

    async reloadCompanyAdditionalIncomes(): Promise<void> {
        this.editForm?.reloadCompanyAdditionalIncomes();
    }

    /**
     * Populate filter groups that have values coming from the database
     */
    async populateDynamicFilters(): Promise<void> {
        const additionalIncomeRequest = CompanyAdditionalIncome.param('company', this.auth.company.id).where(
            'active',
            true
        );
        const departmentRequest = Department.param('company', this.auth.company.id);
        const officeLocationRequest = Office.param('company', this.auth.company.id);
        const [[additionalIncome], [department], [officeLocation]] = await Promise.all([
            additionalIncomeRequest.get(),
            departmentRequest.get(),
            officeLocationRequest.get(),
        ]);
        this.populateFilter(additionalIncome, 'displayName', 'name', this.additionalIncomeFilterGroup);
        this.populateFilter(department, 'name', 'id', this.departmentFilterGroup);
        this.populateFilter(officeLocation, 'name', 'id', this.officeFilterGroup);
    }

    /**
     * Populate a filter group with data from the database
     */
    populateFilter<T>(models: T[], labelAttribute: keyof T, valueAttribute: keyof T, filterGroup: FilterGroup): void {
        for (const model of models) {
            if (model[valueAttribute]) {
                const filterOption = {
                    label: String(model[labelAttribute]),
                    value: String(model[valueAttribute]),
                    selected: false,
                };

                filterGroup.options.push(filterOption);
            }
        }
    }

    /**
     * Configure additional filters for recurring payrolls
     */
    configureFiltersForRecurring(): void {
        this.filterGroups.push({
            ...FILTERS.WAGE_TYPE,
            options: [
                { label: 'payroll.run.hourly', value: 'hourly', selected: false },
                { label: 'payroll.run.salary', value: 'salary', selected: false },
            ],
        });

        this.paymentsFilterGroup.options = [
            { label: 'payroll.run.reimbursement', value: 'reimbursement', selected: false },
            { label: 'payroll.run.prorated', value: 'prorated', selected: false },
        ];
    }

    /**
     * Add an option to filter employees that are on leave
     */
    addLeaveOfAbsenceStatusFilterOption(): void {
        this.statusFilterGroup.options.push({
            label: 'payroll.run.onLeave',
            value: 'onLeave',
            selected: false,
        });
    }

    async uploadHours(file?: File): Promise<void> {
        try {
            if (!file) {
                (document.querySelector('[name="fileSelector"]') as HTMLElement).click();
                return;
            }

            this.isLoading = true;
            await this.hoursImporterService.uploadHours(this.getPayroll().id, file);
            this.analytics.trackEvent(AnalyticEvents.PayrollRecurringPayrollEditStepUploadHoursSave);
            this.paystubService.resetQuery();
            await this.paystubService.load();
        } catch (e) {
            this.notify.error('payroll.run.anErrorOccurredWhileUploadingTheHours');
        }

        // Clear the selection
        (document.querySelector('[name="fileSelector"]') as HTMLInputElement).value = '';

        this.isLoading = false;
    }

    async downloadHoursTemplate(): Promise<void> {
        this.isLoading = true;
        await this.hoursImporterService.downloadTemplate(this.getPayroll().id);
        this.analytics.trackEvent(AnalyticEvents.PayrollRecurringPayrollEditStepUploadHoursDownload);
        this.isLoading = false;
    }

    async downloadAdditionalIncomesTemplate(): Promise<void> {
        this.isLoading = true;
        await this.importAdditionalIncomesService.downloadTemplate(this.getPayroll().id);
        this.analytics.trackEvent(AnalyticEvents.PayrollRecurringPayrollEditStepUploadAdditionalIncomesDownload);
        this.isLoading = false;
    }

    async uploadAdditionalIncomes(file?: File): Promise<void> {
        try {
            if (!file) {
                (document.querySelector('[name="fileSelectorIncomes"] [name="fileSelector"]') as HTMLElement).click();
                return;
            }

            this.isLoading = true;
            await this.importAdditionalIncomesService.uploadAdditionalIncomes(this.getPayroll().id, file);
            this.analytics.trackEvent(AnalyticEvents.PayrollRecurringPayrollEditStepUploadAdditionalIncomesSave);
            this.paystubService.resetQuery();
            await this.paystubService.load();
        } catch (e) {
            this.notify.error('payroll.run.anErrorOccurredWhileUploadingTheIncomes');
        }

        // Clear the selection
        (document.querySelector('[name="fileSelectorIncomes"] [name="fileSelector"]') as HTMLInputElement).value = '';

        this.isLoading = false;
    }

    async uploadAmeegoHumiImport(file?: File): Promise<void> {
        try {
            if (!file) {
                (
                    document.querySelector('[name="fileSelectorAmeegoHumi"] [name="fileSelector"]') as HTMLElement
                ).click();
                return;
            }

            this.isLoading = true;
            this.ameegoHumiImportErrors = await this.ameegoHumiImportService.uploadAmeegoHumiImport(
                this.getPayroll().id,
                file
            );
            this.ameegoHumiImportErrorsModalRows = this.ameegoHumiImportErrors.map((error) => ({
                cells: [error['employeeName'], error['message']],
            }));
            this.analytics.trackEvent(AnalyticEvents.PayrollRecurringPayrollEditStepAmeegoHumiImportSave);
            this.paystubService.resetQuery();
            await this.paystubService.load();
            await this.reloadCompanyAdditionalIncomes();
        } catch (e) {
            this.notify.error(e);
        }

        // Clear the selection
        (document.querySelector('[name="fileSelectorAmeegoHumi"] [name="fileSelector"]') as HTMLInputElement).value =
            '';

        this.isLoading = false;
    }

    async ngOnInit(): Promise<void> {
        this.populateDynamicFilters();
        await this.getFeatureFlags();

        this.setPayrollView();
        this.addLeaveOfAbsenceStatusFilterOption();

        const payroll = await this.payrollService.get(this.route.snapshot.params['payroll']);

        if (!payroll) {
            this.router.navigate(['/payroll', 'run']);
            return;
        }

        this.subscriptions.push(
            this.settingsService.settings$.subscribe((settings) => {
                this.settings = settings;
            })
        );

        this.paystubService.setPayroll(payroll);

        await this.paystubService.load(this.showTableView);

        if (payroll.recurring) {
            this.configureFiltersForRecurring();
        }

        this.stepIndices = {
            hoursStep: null,
            editStep: 0,
            reviewStep: 1,
        };

        switch (payroll.state) {
            case PayrollStates.Locked:
            case PayrollStates.Paid:
            case PayrollStates.Paying:
            case PayrollStates.Processing:
            case PayrollStates.ProcessingTimeOff:
                this.notify.error('payroll.run.payrollIsNotInEditableState');
                this.router.navigate(['/payroll/run']);
                break;
            case PayrollStates.Pending:
                this.initialStep = this.stepIndices.reviewStep;
                break;
        }

        this.payroll = payroll;

        await this.settingsService.load();

        await this.setHoursToggles();

        this.setTimeOffSettings();

        if (this.isUsingTimeWorkedImportPolling) {
            this.subscriptions.push(
                this.checkImportService.checkImportStatusOf$(this.payroll).subscribe((timeWorkedImports) => {
                    const previousImportCount = this.importsInProcess.length;

                    this.importsInProcess = timeWorkedImports.filter(isOngoing);
                    this.setNextStepDisabledState(this.importsInProcess.length > 0);

                    // If imports just finished, show danger banner and try reloading the page
                    if (previousImportCount > 0 && this.importsInProcess.length === 0) {
                        location.reload();
                        this.importsJustFinished = true;
                    }
                })
            );
        }

        // Check for WCB backfills and disable next button if any are running
        this.subscriptions.push(
            this.checkForWcbBackfillService.checkBackfillStatusOf$(this.payroll).subscribe((isBackfillInProgress) => {
                const backfillWasInProgress = this.isWcbBackfillInProgress;

                this.isWcbBackfillInProgress = isBackfillInProgress;
                this.setNextStepDisabledState(isBackfillInProgress);

                if (!isBackfillInProgress && backfillWasInProgress) {
                    this.wcbBackfillJustFinished = true;
                }
            })
        );

        this.enableLazyLoadScrolling();
        await this.loadSchedule();
        this.isLoading = false;
    }

    setNextStepDisabledState(shouldNextStepBeDisabled: boolean): void {
        this.disableUserFromClickingNextStep = shouldNextStepBeDisabled;
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }

        if (this.isUsingTimeWorkedImportPolling) {
            this.checkImportService.stopCheckingImportStatus();
        }

        this.checkForWcbBackfillService.stopCheckingBackfillStatus();
    }

    async addPaystub(): Promise<void> {
        if (!this.addEmployee) {
            return;
        }

        try {
            this.isLoading = true;
            await this.payrollService.addPaystub(this.addEmployee, this.getPayroll());
            await this.paystubService.load();
        } catch (e) {
            this.notify.error(e);
        }
        this.isLoading = false;
    }

    onReset = async (): Promise<void> => {
        this.isLoading = true;

        try {
            await this.payrollService.reset(this.getPayroll());

            this.paystubService.resetQuery();
            await this.paystubService.load();

            this.isLoading = false;
        } catch (e) {
            this.notify.error(e);
            this.isLoading = false;
            throw e;
        }
    };

    async asyncRunPayroll(): Promise<void> {
        this.updateProgress({ total: 0, completed: 0 });

        try {
            await this.payrollService.asyncRun(this.getPayroll());
        } catch (error) {
            this.notify.error(error);

            this.isLoading = false;
        }
    }

    finishRunPayroll = async (): Promise<void> => {
        await this.reloadPayroll();

        this.updateProgress({ total: 1, completed: 1 });
        this.hasPayrollRunCompleted = true;

        this.paystubService.resetQuery();
        await this.paystubService.load();

        this.isLoading = false;
    };

    payrollRunError = async (errorMessage: unknown): Promise<void> => {
        await this.reloadPayroll();

        this.notify.error(errorMessage);

        this.paystubService.resetQuery();
        await this.paystubService.load();

        this.isLoading = false;
    };

    /**
     * Reload the current payroll
     *
     * This is a wrapper function around reloading the current payroll, which ensures that
     * `undefined` is never assigned to `this.payroll`. Of course, this shouldn't happen
     * if the current payroll has a valid id, but good to know if it does...
     */
    async reloadPayroll(): Promise<void> {
        const payroll = await this.payrollService.get(this.getPayroll().id);

        if (!payroll) {
            throw new Error('Payroll disappeared');
        }

        this.payroll = payroll;
    }

    // Make a service call to change the state of the payroll
    async doStateChange(serviceCall: (payroll: Payroll) => Promise<JsonApiResponse<null>>): Promise<void> {
        this.isLoading = true;

        try {
            await serviceCall(this.getPayroll());
            this.paystubService.resetQuery();
            await this.paystubService.load();

            this.isLoading = false;
        } catch (e) {
            this.notify.error(e);
            this.isLoading = false;
            throw e;
        }
    }

    async onPay(): Promise<void> {
        this.isLoading = true;

        this.trackQuebecPayrollPaid();

        this.router.navigate(['/payroll', 'run', this.getPayroll().id, 'paying']);

        this.isLoading = false;
    }

    async onSave(): Promise<void> {
        if (this.isHoursUnlocked) {
            return;
        }

        const confirmPay = await this.completeAndPayConfirmation.open();

        if (!confirmPay) {
            return;
        }

        this.onPay();
    }

    async onDelete(): Promise<void> {
        const confirm = await this.confirmDeleteDialog.open();

        if (!confirm) {
            return;
        }

        try {
            this.isLoading = true;
            await this.getPayroll().param('company', this.auth.company.id).delete();

            this.notify.success('success');
            this.router.navigate(['../'], { relativeTo: this.route });
        } catch (e) {
            this.isLoading = false;
            this.notify.error(e);
        }
    }

    toggleExportMenu(): void {
        this.expandExportMenu = !this.expandExportMenu;
    }

    toggleImportMenu(): void {
        this.expandImportPayrollData = !this.expandImportPayrollData;
    }

    employeeStatusDeterminer: (employee: Employee) => OptionStatus | null = (e: Employee) => {
        if (e.status === 'terminated') {
            return { type: 'danger', label: 'payroll.run.terminated' };
        }

        return null;
    };

    async onPayrollChange(): Promise<void> {
        await this.paystubService.load();
    }

    async onSort(direction: SortDirection): Promise<void> {
        this.isLoading = true;
        this.paystubService.setSortDirection(direction);
        await this.paystubService.load();
        this.isLoading = false;
    }

    // This is really a method, not a property
    // ESLint wants it to be up with the properties
    // eslint-disable-next-line @typescript-eslint/member-ordering
    onSearch = debounce(async (query?: string): Promise<void> => {
        this.isLoading = true;
        this.paystubService.setSearchQuery(query);
        await this.paystubService.load();
        this.isLoading = false;
    }, DEBOUNCE_TIME);

    /**
     * Reload the paystubs with the current filter values
     */
    async onFilter(): Promise<void> {
        this.isLoading = true;
        this.paystubService.setActiveFilters(filterGroupsToFilterCategories(this.filterGroups));
        await this.paystubService.load();
        this.isLoading = false;
    }

    getYear(date: string): number {
        return getYear(parse(date));
    }

    getProgress(): number {
        if (this.total === 0) {
            return 0;
        }

        return Math.round((this.completed / this.total) * 100);
    }

    async setHoursToggles(): Promise<void> {
        const [hasTimeIntegrations, hasHoursRecords] = await Promise.all([
            this.hasTimeIntegrations(),
            this.hasHoursRecords(),
        ]);

        // If time integrations are not set up or employee hours do not exist for this payroll
        // then Complete and Pay should not be blocked based on the hoursLocked value
        // If there are any hours assigned to this payroll, then block Complete and Pay
        this.isHoursUnlocked = hasTimeIntegrations && hasHoursRecords && !this.getPayroll().hoursLocked;
        this.showHoursStep = hasTimeIntegrations && !!this.settings?.showHoursPage;
    }

    async setPayrollView(): Promise<void> {
        const hasTableView: boolean | null = await this.localStorageService.retrieve('showTableView');

        // Determine if the table view should be shown
        if (isNil(hasTableView)) {
            this.showTableView = this.hasAgGridFeature;
            this.localStorageService.store('showTableView', this.showTableView);
        } else if (this.hasAgGridFeature) {
            this.showTableView = hasTableView;
        }
    }

    async switchView(): Promise<void> {
        const hasTableView: boolean | null = await this.localStorageService.retrieve('showTableView');
        this.localStorageService.store('showTableView', !hasTableView);
        this.showTableView = !hasTableView;
        if (!this.showTableView) {
            // while switching back to classic view reload paystubs
            this.isLoading = true;
            this.paystubService.resetQuery();
            await this.paystubService.load();
            this.isLoading = false;
        }
    }

    async submitFeedback(): Promise<void> {
        if (this.feedBackMessage.trim().length > 0) {
            try {
                const endpoint =
                    `${this.feedbackZapierUrl}?` +
                    $.param({
                        feedback: this.feedBackMessage,
                        companyId: this.auth.company.id,
                        companyName: this.auth.company.name,
                        userName: this.auth.employee.fullName,
                        userEmail: this.auth.employee.account.email,
                    });

                await this.http.get(endpoint).toPromise();
                this.notify.success('payroll.run.table.feedbackSubmit');
            } catch (err) {
                this.notify.error('payroll.run.table.requestError');
            } finally {
                this.feedBackMessage = '';
                this.isFeedbackModalOpen = false;
            }
        }
    }

    private updateProgress(progress: ProgressBarStatus): void {
        this.completed = progress.completed;
        this.total = progress.total;
    }

    private enableLazyLoadScrolling(): void {
        setTimeout(() => {
            this.scrollObserver = new IntersectionObserver(async ([entry]) => {
                if (!entry.isIntersecting) {
                    return;
                }

                if (!this.paystubService.hasMore) {
                    return;
                }

                if (this.isLazyLoading) {
                    return;
                }

                this.isLazyLoading = true;
                await this.paystubService.loadMore(this.showTableView);

                this.isLazyLoading = false;
            });

            this.scrollObserver.observe(this.scrollAnchor.nativeElement);
        });
    }

    private setTimeOffSettings(): void {
        const steps: Step[] = [];
        if (this.showHoursStep) steps.push('hoursStep');
        steps.push('editStep');
        steps.push('reviewStep');

        Object.assign(this.stepIndices, Object.fromEntries(steps.map((step, index) => [step, index])));

        // Since the step indeices have changed, we'll reassign the initial step based on the new indices
        // so that we don't end up with something unexpected.
        switch (this.getPayroll().state) {
            case PayrollStates.Pending:
                this.initialStep = this.stepIndices.reviewStep;
                break;
        }
    }

    private hasTerminatedEmployeeOnOffCycle(payroll: Payroll, paystubs: Paystub[]): boolean {
        if (payroll.recurring) {
            return false;
        }

        return paystubs.some((paystub: Paystub) => paystub.employee && paystub.employee.isTerminated);
    }

    private trackQuebecPayrollPaid(): void {
        const quebecPaystubCount = this.payroll?.qcPaystubCount;
        if (!quebecPaystubCount) {
            return;
        }

        this.analytics.trackEvent(AnalyticEvents.PayrollQuebecReviewStepPayNow, { quebecPaystubCount });
    }

    private getSchedulePath(): string {
        return PayrollResources.Schedule.replace(':company', this.auth.company.id.toString());
    }

    private async loadSchedule(): Promise<void> {
        try {
            const res = await this.http.get<{ data: { attributes: object } }>(this.getSchedulePath()).toPromise();
            this.schedule = new Schedule(res['data']['attributes']);
        } catch (err) {
            this.notify.error(err || 'payroll.run.couldNotLoadSchedule');
            this.isLoading = false;
        }
    }

    private async hasTimeIntegrations(): Promise<boolean> {
        const res = await this.http
            .get<{
                data: { hasTimeIntegrations: boolean };
            }>(PayrollResources.HasTimeIntegrations.replace(':company', this.auth.company.id.toString()))
            .toPromise();

        return res['data']['hasTimeIntegrations'];
    }

    private async hasHoursRecords(): Promise<boolean> {
        const [hours] = await EmployeeHour.param('company', this.auth.company.id)
            .param('payroll', this.getPayroll().id)
            .get();

        return hours.length > 0;
    }

    /**
     * A getter for the current payroll so we don't have to keep checking for undefined when calling local methods
     *
     * Will throw if the payroll is undefined when trying to perform an action, which would be a bug
     */
    private getPayroll(): Payroll {
        if (!this.payroll) {
            throw new Error('Payroll undefined');
        }

        return this.payroll as Payroll;
    }
}
